import { useEffect, useMemo, useState } from 'react';
import { ReactComponent as InfoIcon } from '../../assets/icons/icon-info.svg';

import styles from './PortCard.module.scss';
import { CountryFlag } from 'CountryFlag/CountryFlag';

import { Link } from 'react-router-dom';
import { useLazyGetWeatherByPortKeyQuery } from 'app/api/marine-infrastructure';
import { WeatherBlock } from '../WeatherBlock';
import { Icon } from '../Icon';
import 'moment-timezone';
import { utc } from 'app/helpers/timeHelper';
import { useTranslation } from 'react-i18next';
import { InfoWrapper } from '../AISCard';
import { useLazyGetPortCardQuery } from 'app/api/facade';
import { VesselsCount } from '../VesselsCount';
import { PortCardData } from 'ais-facade-api/api';
import { LocalTime } from '../LocalTime';
import { glToStringHDMS } from 'app/helpers';
import { useAppSelector } from 'app/store/store';
import { useAlertHook } from 'app/hooks/useAlertHook';

interface Props {
  portKey: string;
  shown: boolean;
  isHistoryMode?: boolean;
  onClose: () => void;
  onClick: () => void;
  toggleVesselsCatalog: () => void;
}

export const PortCard = ({ portKey, onClose, onClick, shown, isHistoryMode, toggleVesselsCatalog }: Props) => {
  const { t } = useTranslation();
  const { addAlert } = useAlertHook();

  const [getPortCardData] = useLazyGetPortCardQuery();
  const [portData, setPortData] = useState<PortCardData>();
  const { lat, locode, lon, name, un, meta, countryCode, mmsiCount, characteristics } = portData || {};
  const { entWidth, channelDepth, vesselLength, vesselBeam, vesselDraft } = characteristics || {};
  const [getWeatherByPort, { data: portWeather }] = useLazyGetWeatherByPortKeyQuery();

  const profile = useAppSelector((state) => state.userSlice.profile);
  const coordinatesFormat = useMemo(() => profile?.ui?.['coordinatesFormat'] ?? 'DMS', [profile]);

  useEffect(() => {
    if (getPortCardData) {
      getPortCardData(portKey).then((response) => {
        const data = response?.data?.result;
        setPortData(data);
      });
    }
  }, [getPortCardData, portKey]);

  useEffect(() => {
    if (portKey && !isHistoryMode) {
      getWeatherByPort(portKey);
    }
  }, [portKey, isHistoryMode]);

  const position: string | undefined = useMemo(() => {
    if (typeof lat !== 'number' || typeof lon !== 'number') {
      return undefined;
    }
    return glToStringHDMS([lon, lat], coordinatesFormat);
  }, [lat, lon, coordinatesFormat]);

  return (
    <>
      {shown && portData && (
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <div className={styles.name}>
              <Icon name="port" size="16px" className={styles.card_icon} />
              {name}
            </div>

            <div onClick={onClose} className={styles.close}>
              <Icon name="close-16" size="16px" />
            </div>
          </div>
          <div className={styles.sub_header}>{t('Port')}</div>

          <div className={styles.content}>
            <InfoWrapper
              label={`${t('Country')}:`}
              value={
                typeof meta?.countryName !== 'undefined' ? (
                  <>
                    {countryCode && <CountryFlag countryCode={countryCode.toLowerCase()} />}
                    <strong>
                      {meta?.countryName} ({countryCode})
                    </strong>
                  </>
                ) : (
                  <div className={styles.no_data}>&mdash;</div>
                )
              }
            />
            <InfoWrapper label="UN/LOCODE:" value={`${un} ${locode}`} />
            <InfoWrapper label={`${t('LocalTime')}:`} value={<LocalTime lat={lat} lon={lon} />} />
            <InfoWrapper
              label={`${t('Coordinates')}:`}
              value={position}
              valueIcon={<Icon name="aim" size="14px" />}
              clickable={!!position}
              onClick={onClick}
            />
            {!isHistoryMode && (
              <InfoWrapper
                label={`${t('PortTargetCatalog')}:`}
                onClick={toggleVesselsCatalog}
                clickable={Boolean(mmsiCount && mmsiCount > 0)}
                value={<VesselsCount mmsiCount={mmsiCount} />}
              />
            )}
            <InfoWrapper
              label={`${t('EntranceWidth')}:`}
              value={typeof entWidth === 'number' ? entWidth + '\u0020' + t('MeterUnit') : undefined}
            />
            <InfoWrapper
              label={`${t('ChannelDepth')}:`}
              value={typeof channelDepth === 'number' ? channelDepth + '\u0020' + t('MeterUnit') : undefined}
            />
            <InfoWrapper
              label={`${t('VesselLength')}:`}
              value={typeof vesselLength === 'number' ? vesselLength + '\u0020' + t('MeterUnit') : undefined}
            />
            <InfoWrapper
              label={`${t('VesselBeam')}:`}
              value={typeof vesselBeam === 'number' ? vesselBeam + '\u0020' + t('MeterUnit') : undefined}
            />
            <InfoWrapper
              label={`${t('VesselDraft')}:`}
              value={typeof vesselDraft === 'number' ? vesselDraft + '\u0020' + t('MeterUnit') : undefined}
            />
            <InfoWrapper
              label={`API ID:`}
              value={
                <div title={t('ClipboardCopy')} className={styles.copy_id}>
                  <span>{portKey}</span>
                  <Icon name="copy" size="14px" />
                </div>
              }
              clickable
              onClick={() => {
                portKey &&
                  navigator.clipboard.writeText(portKey).then(() => {
                    addAlert({
                      data: {
                        messages: [{ description: t('ClipboardCopySuccess'), type: 'information' }],
                      },
                    });
                  });
              }}
            />
            <div className={styles.divider} />
            {portWeather?.weather && Object.keys(portWeather.weather).length !== 0 && (
              <WeatherBlock weather={portWeather.weather} full={false} utc={lat && lon ? `(UTC${utc(lat, lon)})` : null} />
            )}
            <div className={styles.info_wrapper}>
              <Link to={`/ports/${portKey}`} className={styles.info_link}>
                <InfoIcon />
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
